import React, { useState } from "react";
import MultiSelectDropdown from "../../../components/ui/MultiSelectDropdown";
import DateRangePicker from "../../../components/ui/DateRangePicker";
import TimeRangePicker from "../../../components/ui/TimeRangePicker";
import DateRangePresets from "../../../features/filter/TabFilterModal/DateRangePresets";

const TotalDowntimeKPISettings = ({
  title,
  objectTypes,
  objectTypeActivities,
  selectedObjectType,
  cycleStartActivity,
  cycleEndActivity,
  availableResources,
  selectedResources,
  dateRange,
  shiftStart,
  shiftEnd,
  breaks,
  toleranceThreshold,
  ignoreShortTransitions,
  includeNonWorkingDays,
  includeNoDataResources,
  multiResourceMode,
  onTitleChange,
  onObjectTypeChange,
  onCycleStartActivityChange,
  onCycleEndActivityChange,
  onResourceSelectionChange,
  onDateRangeChange,
  onShiftStartChange,
  onShiftEndChange,
  onAddBreak,
  onRemoveBreak,
  onUpdateBreak,
  onToleranceThresholdChange,
  onIgnoreShortTransitionsChange,
  onIncludeNonWorkingDaysChange,
  onIncludeNoDataResourcesChange,
  onMultiResourceModeChange,
  loading = false,
}) => {
  const [thresholdInput, setThresholdInput] = useState(String(toleranceThreshold ?? 0));
  const activitiesForType =
    (objectTypeActivities || {})[selectedObjectType] || [];

  return (
    <div className="space-y-6">
      {/* KPI Title */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Title
        </label>
        <input
          type="text"
          value={title}
          onChange={(e) => onTitleChange(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          placeholder="Total Downtime"
          disabled={loading}
        />
        <p className="text-xs text-gray-500 mt-1">
          Display name for this KPI tile
        </p>
      </div>

      {/* Object Type Selection */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-4">
        <label className="block text-sm font-medium text-gray-700">
          Object Type and Resources
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Object Type Dropdown */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Object Type *
            </label>
            <select
              value={selectedObjectType}
              onChange={(e) => onObjectTypeChange(e.target.value)}
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              disabled={loading}
            >
              <option value="">Select Object Type</option>
              {(objectTypes || []).map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>
          {/* Resource Selection */}
          <div>
            <MultiSelectDropdown
              label="Resources *"
              options={availableResources.map(String)}
              selectedValues={selectedResources}
              onSelectionChange={onResourceSelectionChange}
              placeholder="Select resources..."
              disabled={loading || !selectedObjectType}
              itemType="resources"
              className="w-full"
            />
          </div>
        </div>
        <p className="text-xs text-gray-500">
          Select the object type and one or more resources to analyze
        </p>
      </div>

      {/* Date Range */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
        <DateRangePicker
          startDate={dateRange.startDate}
          endDate={dateRange.endDate}
          onChange={onDateRangeChange}
          hint="Filter events within this date range. Leave empty to include all dates."
          disabled={loading}
        />
        <DateRangePresets
          value={{ startDate: dateRange.startDate, endDate: dateRange.endDate, enabled: !!(dateRange.startDate || dateRange.endDate) }}
          onChange={(v) =>
            onDateRangeChange({
              startDate: (v.startDate || "").slice(0, 10),
              endDate: (v.endDate || "").slice(0, 10),
            })
          }
        />
      </div>

      {/* Shift Configuration */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <TimeRangePicker
          startTime={shiftStart}
          endTime={shiftEnd}
          onStartChange={onShiftStartChange}
          onEndChange={onShiftEndChange}
          label="Shift Schedule *"
          hint="Define the working shift window for downtime calculation"
          disabled={loading}
        />
      </div>

      {/* Breaks */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <label className="block text-sm font-medium text-gray-700">
            Breaks
          </label>
          <button
            onClick={onAddBreak}
            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-primary-600 bg-primary-50 border border-primary-200 rounded-md hover:bg-primary-100 transition-colors"
            disabled={loading}
          >
            <svg
              className="w-3 h-3"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            Add Break
          </button>
        </div>
        {breaks.length === 0 ? (
          <p className="text-xs text-gray-500 italic">
            No breaks configured. Breaks are subtracted from shift time.
          </p>
        ) : (
          <div className="space-y-2">
            {breaks.map((brk, idx) => (
              <div
                key={idx}
                className="flex items-center gap-2 bg-white border border-gray-200 rounded-md p-2"
              >
                <TimeRangePicker
                  startTime={brk.start}
                  endTime={brk.end}
                  onStartChange={(val) => onUpdateBreak(idx, "start", val)}
                  onEndChange={(val) => onUpdateBreak(idx, "end", val)}
                  startLabel="From"
                  endLabel="To"
                  disabled={loading}
                  className="flex-1"
                />
                <button
                  onClick={() => onRemoveBreak(idx)}
                  className="flex-shrink-0 p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                  title="Remove break"
                  disabled={loading}
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Divider */}
      <div className="border-t border-gray-200"></div>

      {/* Advanced Settings */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Multi-Resource Mode
        </label>
        <select
          value={multiResourceMode}
          onChange={(e) => onMultiResourceModeChange(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          disabled={loading}
        >
          <option value="accumulated">Accumulated</option>
          <option value="simultaneous">Simultaneous</option>
        </select>
        <p className="text-xs text-gray-500 mt-1">
          How to aggregate downtime across resources
        </p>
      </div>

      <div>
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={ignoreShortTransitions}
            onChange={(e) => onIgnoreShortTransitionsChange(e.target.checked)}
            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            disabled={loading}
          />
          <span className="text-sm font-medium text-gray-700">
            Ignore short transition times between cycles
          </span>
        </label>
        <p className="text-xs text-gray-500 mt-1 ml-6">
          When enabled, short gaps between cycles are not
          counted as downtime
        </p>
      </div>

      {ignoreShortTransitions && (
        <div className="ml-6 bg-gray-50 border border-gray-200 rounded-lg p-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Maximum allowed transition time (seconds)
          </label>
          <input
            type="number"
            value={thresholdInput}
            onChange={(e) => {
              const raw = e.target.value;
              setThresholdInput(raw);
              if (raw === '') return;
              const parsed = parseInt(raw, 10);
              if (!isNaN(parsed)) {
                onToleranceThresholdChange(Math.max(0, parsed));
              }
            }}
            onBlur={() => {
              const parsed = parseInt(thresholdInput, 10);
              const safe = isNaN(parsed) ? 0 : Math.max(0, parsed);
              setThresholdInput(String(safe));
              onToleranceThresholdChange(safe);
            }}
            min="0"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            disabled={loading}
          />
          <p className="text-xs text-gray-500 mt-1">
            Time between two cycles that is still considered productive
          </p>
        </div>
      )}

      <div>
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={includeNonWorkingDays}
            onChange={(e) => onIncludeNonWorkingDaysChange(e.target.checked)}
            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            disabled={loading}
          />
          <span className="text-sm font-medium text-gray-700">
            Include Non-Working Days
          </span>
        </label>
        <p className="text-xs text-gray-500 mt-1 ml-6">
          Include weekends and holidays in the analysis — counted as full shift downtime
        </p>
      </div>

      <div>
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={includeNoDataResources}
            onChange={(e) => onIncludeNoDataResourcesChange(e.target.checked)}
            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            disabled={loading}
          />
          <span className="text-sm font-medium text-gray-700">
            Count resources without data as full downtime
          </span>
        </label>
        <p className="text-xs text-gray-500 mt-1 ml-6">
          When enabled, selected resources with no records in the dataset are
          treated as 100% down for every analyzed day
        </p>
      </div>

      {/* Info box */}
      <div className="p-3 bg-primary-50 border border-primary-200 rounded-lg">
        <div className="flex items-start gap-2">
          <svg
            className="w-4 h-4 text-primary-600 mt-0.5 flex-shrink-0"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
              clipRule="evenodd"
            />
          </svg>
          <p className="text-xs text-primary-700">
            Downtime is calculated as the time within each shift where no cycle
            activity occurs, minus any configured breaks. Use "Ignore Short
            Transitions" to exclude brief gaps caused by normal cycle
            transitions.
          </p>
        </div>
      </div>
    </div>
  );
};

export default TotalDowntimeKPISettings;
